/**
 * useSetRsvp Hook
 *
 * Sets RSVP for an event using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const SetEventRsvpDocument = `
  mutation SetEventRsvp($eventId: ID!, $status: RsvpStatus!) {
    setEventRsvp(eventId: $eventId, status: $status) {
      id
      status
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface Rsvp {
  id: string;
  status: string;
}

interface UseSetRsvpResult {
  setRsvp: (eventId: string, status?: string) => Promise<Rsvp>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Set RSVP for an event.
 *
 * Uses URQL mutation for setting event RSVP status.
 */
export function useSetRsvp(): UseSetRsvpResult {
  const [result, executeMutation] = useMutation<
    { setEventRsvp: Rsvp },
    { eventId: string; status: string }
  >(SetEventRsvpDocument);

  const setRsvp = useCallback(
    async (eventId: string, status: string = "GOING"): Promise<Rsvp> => {
      try {
        const response = await executeMutation({ eventId, status });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to set RSVP"
          );
        }

        if (!response.data?.setEventRsvp) {
          throw new Error("No RSVP returned from server");
        }

        errorTracking.addBreadcrumb({
          category: "event",
          message: "RSVP set",
          data: { eventId, status },
        });

        return response.data.setEventRsvp;
      } catch (err) {
        const rsvpError =
          err instanceof Error ? err : new Error("Failed to set RSVP");

        errorTracking.captureError(rsvpError, {
          tags: { feature: "events", action: "setRsvp" },
          extra: { eventId, status },
        });

        throw rsvpError;
      }
    },
    [executeMutation]
  );

  return {
    setRsvp,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
